import { useNavigate } from "react-router-dom";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from "src/components/Iconify";
import { verifyRules, hasFeatureAccess, FEATURE_KEYS } from "src/utils/functions";
import { Sheet, SheetContent, SheetTrigger } from "src/@/components/ui/sheet";
import { SidebarContext, SidebarMenuButton, SidebarGroup } from "src/components/dashboard/SidebarMenuButton";
import useAuth from "src/hooks/useAuth";

export default function SidebarDashboard({ user }) {
    const navigate = useNavigate();
    const { t } = useTranslation('settings');
    const { cliente } = useAuth();
    const isAdmin = user?.level === "root";
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const isExpanded = !isCollapsed || isHovered;

    const renderNavContent = (onNavigate) => (
        <nav>
            <SidebarGroup label={t('nav.groups.account')}>
                <SidebarMenuButton
                    title={t('nav.profile')}
                    path="/dashboard/conta/perfil"
                    icon="solar:user-circle-bold"
                    onClick={() => { navigate('/dashboard/conta/perfil'); onNavigate?.(); }}
                />
                {verifyRules(user.rules, "conta.consumo.ver") && (
                    <SidebarMenuButton
                        title={t('nav.consumption')}
                        path="/dashboard/conta/consumo"
                        icon="solar:chart-2-bold"
                        onClick={() => { navigate('/dashboard/conta/consumo'); onNavigate?.(); }}
                    />
                )}
            </SidebarGroup>

            <SidebarGroup label={t('nav.groups.chats')}>
                {(verifyRules(user.rules, "respostas_rapidas") || isAdmin) && (
                    <SidebarMenuButton
                        title={t('nav.quickReplies')}
                        path="/dashboard/configuracoes/respostas-rapidas"
                        icon="solar:chat-square-like-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/respostas-rapidas'); onNavigate?.(); }}
                    />
                )}
                <SidebarMenuButton
                    title={t('nav.tags')}
                    path="/dashboard/configuracoes/tags"
                    icon="solar:tag-bold"
                    onClick={() => { navigate('/dashboard/configuracoes/tags'); onNavigate?.(); }}
                />
                {isAdmin && (
                    <SidebarMenuButton
                        title={t('nav.routings')}
                        path="/dashboard/configuracoes/direcionamentos"
                        icon="solar:routing-2-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/direcionamentos'); onNavigate?.(); }}
                    />
                )}
            </SidebarGroup>

            <SidebarGroup label={t('nav.groups.settings')}>
                {(verifyRules(user.rules, "canais") || isAdmin) && (
                    <SidebarMenuButton
                        title={t('nav.channels')}
                        path="/dashboard/configuracoes/canais"
                        icon="fluent:channel-share-28-filled"
                        onClick={() => { navigate('/dashboard/configuracoes/canais'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "canais") || isAdmin) && (
                    <SidebarMenuButton
                        title={t('nav.socialAccounts')}
                        path="/dashboard/configuracoes/contas-sociais"
                        icon="eva:link-2-outline"
                        onClick={() => { navigate('/dashboard/configuracoes/contas-sociais'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "templates_whatsapp") || verifyRules(user.rules, "templates_messenger") || isAdmin) && (
                    <SidebarMenuButton
                        title={t('nav.templates')}
                        path="/dashboard/configuracoes/templates-whatsapp"
                        icon="solar:chat-square-code-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/templates-whatsapp'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "gerenciar_usuarios") || isAdmin) && (
                    <SidebarMenuButton
                        title={t('nav.users')}
                        path="/dashboard/configuracoes/usuarios"
                        icon="solar:users-group-rounded-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/usuarios'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "gerenciar_setores") || isAdmin) && (
                    <SidebarMenuButton
                        title={t('nav.sectors')}
                        path="/dashboard/configuracoes/setores"
                        icon="mingcute:department-line"
                        onClick={() => { navigate('/dashboard/configuracoes/setores'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "gerenciar_funis") || isAdmin) && (
                    <SidebarMenuButton
                        title={t('nav.funnel')}
                        path="/dashboard/configuracoes/funil-vendas"
                        icon="mdi:funnel-outline"
                        onClick={() => { navigate('/dashboard/configuracoes/funil-vendas'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "gerenciar_integrações") || isAdmin) && (
                    <SidebarMenuButton
                        title={t('nav.integrations')}
                        path="/dashboard/configuracoes/integracoes"
                        icon="solar:plug-circle-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/integracoes'); onNavigate?.(); }}
                    />
                )}
                <SidebarMenuButton
                    title={t('nav.plugins')}
                    path="/dashboard/configuracoes/plugins"
                    icon="solar:widget-bold"
                    onClick={() => { navigate('/dashboard/configuracoes/plugins'); onNavigate?.(); }}
                />
                <SidebarMenuButton
                    title={t('nav.apisWebhooks')}
                    path="/dashboard/configuracoes/apis"
                    icon="solar:key-bold"
                    onClick={() => { navigate('/dashboard/configuracoes/apis'); onNavigate?.(); }}
                />
                {verifyRules(user.rules, "gerenciar_calendario") && (
                    <SidebarMenuButton
                        title={t('nav.calendar')}
                        path="/dashboard/configuracoes/calendarios"
                        icon="solar:calendar-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/calendarios'); onNavigate?.(); }}
                    />
                )}
                {(isAdmin || verifyRules(user.rules, "chatbots.gerenciar")) && (
                    <SidebarMenuButton
                        title={t('nav.chatflows')}
                        path="/dashboard/configuracoes/chatbots"
                        icon="solar:chat-square-code-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/chatbots'); onNavigate?.(); }}
                    />
                )}
                <SidebarMenuButton
                    title={t('nav.tickets')}
                    path="/dashboard/configuracoes/chamados-config"
                    icon="eva:clipboard-outline"
                    onClick={() => { navigate('/dashboard/configuracoes/chamados-config'); onNavigate?.(); }}
                />
                <SidebarMenuButton
                    title={t('nav.tasks')}
                    path="/dashboard/configuracoes/tarefas-config"
                    icon="mdi:clipboard-check-outline"
                    onClick={() => { navigate('/dashboard/configuracoes/tarefas-config'); onNavigate?.(); }}
                />
                {(isAdmin || verifyRules(user.rules, "arquivo.gerenciar_divisoes")) && hasFeatureAccess(cliente, user, FEATURE_KEYS.CHAT_ARCHIVE) && (
                    <SidebarMenuButton
                        title={t('nav.archiving')}
                        path="/dashboard/configuracoes/arquivo-config"
                        icon="eva:archive-outline"
                        onClick={() => { navigate('/dashboard/configuracoes/arquivo-config'); onNavigate?.(); }}
                    />
                )}
                {hasFeatureAccess(cliente, user, FEATURE_KEYS.PROPOSALS) && (
                    <SidebarMenuButton
                        title={t('nav.proposals')}
                        path="/dashboard/configuracoes/propostas-config"
                        icon="mdi:file-document-outline"
                        onClick={() => { navigate('/dashboard/configuracoes/propostas-config'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "canais") || isAdmin) && hasFeatureAccess(cliente, user, FEATURE_KEYS.WEBCHAT) && (
                    <SidebarMenuButton
                        title={t('nav.widgetCenter')}
                        path="/dashboard/configuracoes/widget-cms"
                        icon="solar:widget-add-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/widget-cms'); onNavigate?.(); }}
                    />
                )}
            </SidebarGroup>

            {(isAdmin || verifyRules(user.rules, "agentes_ia")) && (
                <SidebarGroup label={t('nav.groups.ai')}>
                    {isAdmin && (
                        <SidebarMenuButton
                            title={t('nav.copilot')}
                            path="/dashboard/configuracoes/copilot"
                            icon="solar:magic-stick-3-bold"
                            onClick={() => { navigate('/dashboard/configuracoes/copilot'); onNavigate?.(); }}
                        />
                    )}
                    {verifyRules(user.rules, "agentes_ia") && (
                        <SidebarMenuButton
                            title={t('nav.agents')}
                            path="/dashboard/configuracoes/agentes-ia"
                            icon="mingcute:head-ai-fill"
                            onClick={() => { navigate('/dashboard/configuracoes/agentes-ia'); onNavigate?.(); }}
                        />
                    )}
                </SidebarGroup>
            )}

            {(isAdmin || verifyRules(user.rules, "cadastro.gerenciar")) && (
                <SidebarGroup label={t('nav.groups.registry')}>
                    <SidebarMenuButton
                        title={t('nav.services')}
                        path="/dashboard/configuracoes/servicos"
                        icon="material-symbols-light:service-toolbox-rounded"
                        onClick={() => { navigate('/dashboard/configuracoes/servicos'); onNavigate?.(); }}
                    />
                    <SidebarMenuButton
                        title={t('nav.products')}
                        path="/dashboard/configuracoes/produtos"
                        icon="solar:case-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/produtos'); onNavigate?.(); }}
                    />
                </SidebarGroup>
            )}
        </nav>
    );

    return (
        <>
            {/* Mobile: hamburger + Sheet drawer */}
            <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-2">
                <SidebarContext.Provider value={{ isExpanded: true }}>
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                                <Iconify icon="solar:hamburger-menu-linear" width={20} />
                                <span>{t('nav.menu')}</span>
                            </button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-[280px] p-0 pt-8">
                            <div className="flex flex-col flex-1 overflow-y-auto py-2 px-4">
                                {renderNavContent(() => setMobileOpen(false))}
                            </div>
                        </SheetContent>
                    </Sheet>
                </SidebarContext.Provider>
            </div>

            {/* Desktop: original aside sidebar */}
            <SidebarContext.Provider value={{ isExpanded }}>
                <aside
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={`
                    hidden md:flex flex-col flex-shrink-0 border-r border-gray-100 bg-white h-full relative z-20
                    transition-all duration-300 ease-in-out
                    ${isExpanded ? 'w-[260px]' : 'w-[80px]'}
                `}
                >
                    {/* Collapse Button - Absolute on border */}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className={`
                        absolute top-8 -right-3 z-50
                        flex items-center justify-center w-6 h-6 rounded-full
                        bg-white border border-gray-200 shadow-sm
                        text-gray-500 hover:text-gray-900 hover:bg-gray-50
                        transition-all duration-200
                        cursor-pointer
                    `}
                        title={isCollapsed ? t('nav.pinMenu') : t('nav.collapseMenu')}
                    >
                        <Iconify
                            icon={isCollapsed ? "solar:pin-bold" : "solar:alt-arrow-left-linear"}
                            width={14}
                            height={14}
                            className={`transition-transform duration-200 ${isCollapsed ? 'rotate-45' : ''}`}
                        />
                    </button>

                    <div className={`flex flex-col flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-100 hover:scrollbar-thumb-gray-200 ${isExpanded ? 'px-4' : 'px-2'}`}>
                        {renderNavContent()}
                    </div>
                </aside>
            </SidebarContext.Provider>
        </>
    );
}
